import React, { createContext, useContext, useMemo, useState } from 'react';

const LanguageContext = createContext();

// Shared tour-detail and stops translations (added to each language below)
const en_tourDetail = {
  backButton: 'Back to Tours',
  aboutThisTour: 'About This Tour',
  tourRouteStops: 'Tour Route & Stops',
  keyStops: 'Key Stops',
  clickToEnlarge: '(click to enlarge)',
  duration: 'Duration',
  hoursOne: '1 Hour',
  hoursTwo: '2 Hours',
  hoursThree: '2 Hours',
  groupSize: 'Group Size',
  upTo4: 'Up to 4 passengers',
  startingPoint: 'Starting Point',
  startingPointValue: 'Argostoli Center',
  availability: 'Availability',
  availabilityValue: 'Daily from 8:00am to 21:00pm (every hour) – by reservation',
  pricing: 'Pricing',
  passengersCol: 'Passengers',
  priceCol: 'Price',
  personSingular: 'person',
  personPlural: 'persons',
  totalLabel: 'total',
  perPersonUnit: '/person',
  wineryFootnote: '*Entrance to Gentilini winery included in price',
  whatsIncluded: "What's Included",
  included: {
    driver: 'Professional driver/guide',
    pickup: 'Hotel pickup and drop-off',
    fees: 'All fees and taxes',
    water: 'Bottled water',
    photoOps: 'Photo opportunities at scenic spots',
    wifi: 'Free wifi & smartphone charger',
    beachStop: 'Stop for 30-45 mins at any beach you prefer',
    winery: 'Free Priority entrance and tour to the Gentilini winery and vineyards'
  },
  readyToBook: 'Ready to Book This Tour?',
  contactPrompt: 'Contact us to reserve your spot or ask any questions',
  contactUs: 'Contact Us',
  reachUs: 'Reach us faster and easier on Viber or WhatsApp.'
};

const en_stops = {
  iLoveKefalonia: { name: 'I Love Kefalonia', description: 'Iconic photo spot with the famous "I ❤ Kefalonia" sign on the Argostoli waterfront.' },
  ww2Memorial: { name: 'WW2 Memorial', description: 'Memorial honoring the soldiers of the Italian "Acqui" Division.' },
  stTheodoreLighthouse: { name: 'St Theodore Lighthouse', description: 'Historic circular lighthouse with panoramic coastal views.' },
  argostoliSinkholes: { name: 'Argostoli Sinkholes', description: 'Unique geological sinkholes where seawater rushes into the rocks.' },
  centralSquare: { name: 'Central Square', description: 'The heart of Argostoli with cafes, restaurants and local life.' },
  koutavosLagoon: { name: 'Koutavos Lagoon', description: 'Peaceful protected lagoon, home to loggerhead turtles and herons.' },
  deBossetBridge: { name: 'De Bosset Bridge', description: 'Historic stone bridge crossing the Koutavos Lagoon.' },
  carettaCarettaWatchpoint: { name: 'Caretta Caretta Possible Watchpoint', description: 'Spot to watch for the protected Caretta Caretta sea turtles.' },
  makrisGialosBeach: { name: 'Makris Gialos Beach', description: 'Popular sandy beach with crystal-clear waters and beach bars.' },
  platisGialosBeach: { name: 'Platis Gialos Beach', description: 'Golden sand beach with stunning views and facilities.' },
  gentiliniWinery: { name: 'Gentilini Winery', description: 'Priority entrance and guided tour of the renowned Gentilini winery and vineyards.' }
};

export const translations = {
  en: {
    nav: { home: 'Home', tours: 'Tours', gallery: 'Gallery', contact: 'Contact' },
    hero: {
      title: 'Enjoy a tour around Argostoli in a Tuk Tuk.',
      subtitle: 'An unforgetable ride through Hidden gems & iconic sights',
      cta: 'Explore Tours',
      features: [
        'PRIVATE & SMALL GROUP EXPERIENCE (max 4 guests)',
        '100% ELECTRIC ECO-FRIENDLY',
        'PHOTO STOPS',
        'MULTILINGUAL AUDIO GUIDE',
        'CHOOSE YOUR PICK-UP AND DROP-OFF LOCATION'
      ]
    },
    tours: {
      title: '',
      subtitle: 'Choose the perfect tour for your needs',
      exploreMore: 'Explore More',
      backToTours: 'Back to Tours',
      oneHour: {
        title: 'Argostoli City Highlights (1 hour)',
        description: 'Discover the most important highlights of Argostoli through a unique Tuk-Tuk experience.\n\nThe route combines the city\'s most famous hotspots, selected photo stops and a tour around the city streets.\n\nA full view of the history, architecture and local culture of Kefalonia\'s Capital.'
      },
      twoHours: {
        title: 'Argostoli & Lassi Area (2 hours)',
        description: 'This tour combines The standard tour in Argostoli plus a tour on the coastline of Lassi area with an option for a stop for swimming in any of them.\n\nLassi has some of the most famous beaches in Kefalonia. Long sandy beaches with crystal clear water. From well organised beaches with bars, restaurants, sunbeds etc to less crowded peacefull and private beaches, everything is there for you to explore.'
      },
      threeHours: {
        title: 'Argostoli, Lassi & Gentilini Winery (2 hours)',
        duration: '2 Hours',
        description: 'Enjoy a full tour in Argostoli and Lassi with a visit to a local winery where you will have the opportunity to taste selected Kefalonian wines and discover the island\'s rich wine-making tradition and local flavors.\n\nWelcome glass of Prosecco, tutored tasting of 3 unique wines and tasting of 2 extra virgin olive oils and a local balsamic vinegar.\nDuo of Dips with warm pita bread.\nGuided tour in the vineyards and the winery.'
      }
    },
    gallery: { title: '', subtitle: 'Photos from our Tuk Tuk Experience' },
    contact: {
      title: 'Get in Touch', subtitle: 'Contact us for bookings and inquiries',
      name: 'Your Name', email: 'Your Email', phone: 'Phone Number',
      tourSelection: 'Select Tour', tourPlaceholder: 'Choose a tour',
      passengers: 'Number of Passengers', passengersPlaceholder: 'Select passengers',
      date: 'Preferred Date', time: 'Preferred Time', message: 'Your Message',
      send: 'Send Message', sending: 'Sending...',
      success: 'Message sent successfully! We\'ll contact you soon.',
      error: 'Failed to send message. Please try again.'
    },
    footer: {
      about: 'About Us',
      aboutText: 'Explore The city of Argostoli with Eco friendly Tuk Tuk.',
      quickLinks: 'Quick Links', followUs: 'Follow Us', rights: 'All rights reserved.'
    },
    tourDetail: en_tourDetail,
    stops: en_stops
  },
  it: {
    nav: { home: 'Home', tours: 'Tour', gallery: 'Galleria', contact: 'Contatti' },
    hero: {
      title: 'Goditi un tour di Argostoli in un Tuk Tuk.',
      subtitle: 'Un viaggio indimenticabile tra gemme nascoste e luoghi iconici',
      cta: 'Esplora i Tour',
      features: [
        'ESPERIENZA PRIVATA E PICCOLI GRUPPI (max 4 ospiti)',
        '100% ELETTRICO ECO-FRIENDLY',
        'SOSTE FOTOGRAFICHE',
        'GUIDA AUDIO MULTILINGUE',
        'SCEGLI IL TUO PUNTO DI RITIRO E RICONSEGNA'
      ]
    },
    tours: {
      title: '', subtitle: 'Scegli il tour perfetto per le tue esigenze',
      exploreMore: 'Esplora di Più', backToTours: 'Torna ai Tour',
      oneHour: {
        title: 'I Punti Salienti di Argostoli (1 ora)',
        description: 'Scopri i punti salienti più importanti di Argostoli attraverso un\'esperienza unica in Tuk-Tuk.\n\nIl percorso combina i luoghi più famosi della città, soste fotografiche selezionate e un giro per le vie del centro.\n\nUna panoramica completa della storia, dell\'architettura e della cultura locale della capitale di Cefalonia.'
      },
      twoHours: {
        title: 'Argostoli e Zona di Lassi (2 ore)',
        description: 'Questo tour combina il classico giro di Argostoli con un percorso lungo la costa della zona di Lassi, con la possibilità di una sosta per fare il bagno.\n\nLassi vanta alcune delle spiagge più famose di Cefalonia: lunghe spiagge di sabbia con acqua cristallina. Da spiagge ben organizzate con bar, ristoranti e lettini, a spiagge tranquille e appartate, c\'è tutto da esplorare.'
      },
      threeHours: {
        title: 'Argostoli, Lassi e Cantina Gentilini (2 ore)',
        duration: '2 Ore',
        description: 'Goditi un tour completo ad Argostoli e Lassi con una visita a una cantina locale dove avrai l\'opportunità di degustare vini kefaloniaci selezionati e scoprire la ricca tradizione vinicola dell\'isola e i sapori locali.\n\nCalice di benvenuto di Prosecco, degustazione guidata di 3 vini unici e degustazione di 2 oli extravergini d\'oliva e un aceto balsamico locale.\nDuo di Dips con pane pita caldo.\nVisita guidata nei vigneti e nella cantina.'
      }
    },
    gallery: { title: '', subtitle: 'Foto della nostra esperienza in Tuk Tuk' },
    contact: {
      title: 'Mettiti in Contatto', subtitle: 'Contattaci per prenotazioni e informazioni',
      name: 'Il Tuo Nome', email: 'La Tua Email', phone: 'Numero di Telefono',
      tourSelection: 'Seleziona Tour', tourPlaceholder: 'Scegli un tour',
      passengers: 'Numero di Passeggeri', passengersPlaceholder: 'Seleziona passeggeri',
      date: 'Data Preferita', time: 'Ora Preferita', message: 'Il Tuo Messaggio',
      send: 'Invia Messaggio', sending: 'Invio in corso...',
      success: 'Messaggio inviato con successo! Ti contatteremo presto.',
      error: 'Impossibile inviare il messaggio. Riprova.'
    },
    footer: {
      about: 'Chi Siamo',
      aboutText: 'Esplora la città di Argostoli con un Tuk Tuk eco-sostenibile.',
      quickLinks: 'Link Rapidi', followUs: 'Seguici', rights: 'Tutti i diritti riservati.'
    },
    tourDetail: {
      backButton: 'Torna ai Tour',
      aboutThisTour: 'Informazioni sul Tour',
      tourRouteStops: 'Percorso e Tappe del Tour',
      keyStops: 'Tappe Principali',
      clickToEnlarge: '(clicca per ingrandire)',
      duration: 'Durata',
      hoursOne: '1 Ora',
      hoursTwo: '2 Ore',
      hoursThree: '2 Ore',
      groupSize: 'Dimensione del Gruppo',
      upTo4: 'Fino a 4 passeggeri',
      startingPoint: 'Punto di Partenza',
      startingPointValue: 'Centro di Argostoli',
      availability: 'Disponibilità',
      availabilityValue: 'Tutti i giorni dalle 8:00 alle 21:00 (ogni ora) – su prenotazione',
      pricing: 'Prezzi',
      passengersCol: 'Passeggeri',
      priceCol: 'Prezzo',
      personSingular: 'persona',
      personPlural: 'persone',
      totalLabel: 'totale',
      perPersonUnit: '/persona',
      wineryFootnote: '*Ingresso alla cantina Gentilini incluso nel prezzo',
      whatsIncluded: 'Cosa è Incluso',
      included: {
        driver: 'Autista/guida professionale',
        pickup: 'Ritiro e riconsegna in hotel',
        fees: 'Tutte le tasse e i costi inclusi',
        water: 'Acqua in bottiglia',
        photoOps: 'Soste fotografiche nei luoghi panoramici',
        wifi: 'Wifi gratuito e caricabatterie per smartphone',
        beachStop: 'Sosta di 30-45 minuti nella spiaggia che preferisci',
        winery: 'Ingresso prioritario gratuito e visita guidata alla cantina e ai vigneti Gentilini'
      },
      readyToBook: 'Pronto a Prenotare Questo Tour?',
      contactPrompt: 'Contattaci per prenotare il tuo posto o per qualsiasi domanda',
      contactUs: 'Contattaci',
      reachUs: 'Contattaci più velocemente e facilmente su Viber o WhatsApp.'
    },
    stops: {
      iLoveKefalonia: { name: 'I Love Kefalonia', description: 'Iconico punto fotografico con la famosa scritta "I ❤ Kefalonia" sul lungomare di Argostoli.' },
      ww2Memorial: { name: 'Memoriale della Seconda Guerra Mondiale', description: 'Memoriale in onore dei soldati della Divisione Acqui italiana.' },
      stTheodoreLighthouse: { name: 'Faro di San Teodoro', description: 'Storico faro circolare con viste panoramiche sulla costa.' },
      argostoliSinkholes: { name: 'Doline di Argostoli', description: 'Uniche doline geologiche dove l\'acqua di mare si infiltra nelle rocce.' },
      centralSquare: { name: 'Piazza Centrale', description: 'Il cuore di Argostoli con caffè, ristoranti e vita locale.' },
      koutavosLagoon: { name: 'Laguna di Koutavos', description: 'Tranquilla laguna protetta, habitat di tartarughe Caretta Caretta e aironi.' },
      deBossetBridge: { name: 'Ponte De Bosset', description: 'Storico ponte in pietra che attraversa la laguna di Koutavos.' },
      carettaCarettaWatchpoint: { name: 'Possibile Punto di Avvistamento Caretta Caretta', description: 'Punto per osservare le tartarughe marine Caretta Caretta protette.' },
      makrisGialosBeach: { name: 'Spiaggia di Makris Gialos', description: 'Famosa spiaggia di sabbia con acque cristalline e beach bar.' },
      platisGialosBeach: { name: 'Spiaggia di Platis Gialos', description: 'Spiaggia di sabbia dorata con viste mozzafiato e servizi.' },
      gentiliniWinery: { name: 'Cantina Gentilini', description: 'Ingresso prioritario e visita guidata della rinomata cantina e dei vigneti Gentilini.' }
    }
  },
  es: {
    nav: { home: 'Inicio', tours: 'Tours', gallery: 'Galería', contact: 'Contacto' },
    hero: {
      title: 'Disfruta de un tour por Argostoli en un Tuk Tuk.',
      subtitle: 'Un paseo inolvidable entre rincones secretos y lugares icónicos',
      cta: 'Explorar Tours',
      features: [
        'EXPERIENCIA PRIVADA Y GRUPOS PEQUEÑOS (máx 4 huéspedes)',
        '100% ELÉCTRICO ECO-FRIENDLY',
        'PARADAS PARA FOTOS',
        'GUÍA DE AUDIO MULTILINGÜE',
        'ELIJA SU PUNTO DE RECOGIDA Y ENTREGA'
      ]
    },
    tours: {
      title: '', subtitle: 'Elija el tour perfecto para sus necesidades',
      exploreMore: 'Explorar Más', backToTours: 'Volver a los Tours',
      oneHour: {
        title: 'Lo Mejor de Argostoli (1 hora)',
        description: 'Descubre los puntos más importantes de Argostoli a través de una experiencia única en Tuk-Tuk.\n\nLa ruta combina los lugares más famosos de la ciudad, paradas fotográficas seleccionadas y un recorrido por las calles del centro.\n\nUna visión completa de la historia, la arquitectura y la cultura local de la capital de Cefalonia.'
      },
      twoHours: {
        title: 'Argostoli y Zona de Lassi (2 horas)',
        description: 'Este tour combina el recorrido clásico por Argostoli con un paseo por la costa de la zona de Lassi, con la opción de hacer una parada para nadar en cualquiera de ellas.\n\nLassi cuenta con algunas de las playas más famosas de Cefalonia. Largas playas de arena con aguas cristalinas. Desde playas bien organizadas con bares, restaurantes y tumbonas, hasta calas tranquilas y privadas, todo está ahí para explorar.'
      },
      threeHours: {
        title: 'Argostoli, Lassi y Bodega Gentilini (2 horas)',
        duration: '2 Horas',
        description: 'Disfrute de un recorrido completo por Argostoli y Lassi con una visita a una bodega local donde tendrá la oportunidad de degustar vinos kefalonianos seleccionados y descubrir la rica tradición vinícola de la isla y los sabores locales.\n\nCopa de bienvenida de Prosecco, degustación guiada de 3 vinos únicos y degustación de 2 aceites de oliva virgen extra y un vinagre balsámico local.\nDúo de Dips con pan pita caliente.\nVisita guiada por los viñedos y la bodega.'
      }
    },
    gallery: { title: '', subtitle: 'Fotos de nuestra experiencia en Tuk Tuk' },
    contact: {
      title: 'Póngase en Contacto', subtitle: 'Contáctenos para reservas y consultas',
      name: 'Su Nombre', email: 'Su Correo Electrónico', phone: 'Número de Teléfono',
      tourSelection: 'Seleccionar Tour', tourPlaceholder: 'Elija un tour',
      passengers: 'Número de Pasajeros', passengersPlaceholder: 'Seleccione pasajeros',
      date: 'Fecha Preferida', time: 'Hora Preferida', message: 'Su Mensaje',
      send: 'Enviar Mensaje', sending: 'Enviando...',
      success: '¡Mensaje enviado con éxito! Le contactaremos pronto.',
      error: 'Error al enviar el mensaje. Por favor, inténtelo de nuevo.'
    },
    footer: {
      about: 'Sobre Nosotros',
      aboutText: 'Explora la ciudad de Argostoli con un Tuk Tuk ecológico.',
      quickLinks: 'Enlaces Rápidos', followUs: 'Síguenos', rights: 'Todos los derechos reservados.'
    },
    tourDetail: {
      backButton: 'Volver a los Tours',
      aboutThisTour: 'Acerca de Este Tour',
      tourRouteStops: 'Ruta y Paradas del Tour',
      keyStops: 'Paradas Principales',
      clickToEnlarge: '(haga clic para ampliar)',
      duration: 'Duración',
      hoursOne: '1 Hora',
      hoursTwo: '2 Horas',
      hoursThree: '2 Horas',
      groupSize: 'Tamaño del Grupo',
      upTo4: 'Hasta 4 pasajeros',
      startingPoint: 'Punto de Partida',
      startingPointValue: 'Centro de Argostoli',
      availability: 'Disponibilidad',
      availabilityValue: 'Todos los días de 8:00 a 21:00 (cada hora) – con reserva',
      pricing: 'Precios',
      passengersCol: 'Pasajeros',
      priceCol: 'Precio',
      personSingular: 'persona',
      personPlural: 'personas',
      totalLabel: 'total',
      perPersonUnit: '/persona',
      wineryFootnote: '*Entrada a la bodega Gentilini incluida en el precio',
      whatsIncluded: 'Qué Está Incluido',
      included: {
        driver: 'Conductor/guía profesional',
        pickup: 'Recogida y entrega en el hotel',
        fees: 'Todas las tasas e impuestos incluidos',
        water: 'Agua embotellada',
        photoOps: 'Paradas fotográficas en lugares panorámicos',
        wifi: 'Wifi gratis y cargador para smartphone',
        beachStop: 'Parada de 30-45 minutos en la playa que prefieras',
        winery: 'Entrada prioritaria gratuita y visita guiada a la bodega y viñedos Gentilini'
      },
      readyToBook: '¿Listo para Reservar Este Tour?',
      contactPrompt: 'Contáctenos para reservar su lugar o hacer cualquier pregunta',
      contactUs: 'Contáctanos',
      reachUs: 'Contáctenos más rápido y fácilmente en Viber o WhatsApp.'
    },
    stops: {
      iLoveKefalonia: { name: 'I Love Kefalonia', description: 'Icónico punto fotográfico con el famoso cartel "I ❤ Kefalonia" en el paseo marítimo de Argostoli.' },
      ww2Memorial: { name: 'Memorial de la Segunda Guerra Mundial', description: 'Memorial en honor a los soldados de la División italiana "Acqui".' },
      stTheodoreLighthouse: { name: 'Faro de San Teodoro', description: 'Histórico faro circular con vistas panorámicas a la costa.' },
      argostoliSinkholes: { name: 'Sumideros de Argostoli', description: 'Sumideros geológicos únicos donde el agua del mar se cuela entre las rocas.' },
      centralSquare: { name: 'Plaza Central', description: 'El corazón de Argostoli con cafés, restaurantes y vida local.' },
      koutavosLagoon: { name: 'Laguna de Koutavos', description: 'Tranquila laguna protegida, hogar de tortugas bobas y garzas.' },
      deBossetBridge: { name: 'Puente De Bosset', description: 'Histórico puente de piedra que cruza la laguna de Koutavos.' },
      carettaCarettaWatchpoint: { name: 'Posible Punto de Observación Caretta Caretta', description: 'Punto para observar las tortugas marinas Caretta Caretta protegidas.' },
      makrisGialosBeach: { name: 'Playa de Makris Gialos', description: 'Popular playa de arena con aguas cristalinas y bares de playa.' },
      platisGialosBeach: { name: 'Playa de Platis Gialos', description: 'Playa de arena dorada con impresionantes vistas y servicios.' },
      gentiliniWinery: { name: 'Bodega Gentilini', description: 'Entrada prioritaria y visita guiada a la reconocida bodega y viñedos Gentilini.' }
    }
  },
  fr: {
    nav: { home: 'Accueil', tours: 'Tours', gallery: 'Galerie', contact: 'Contact' },
    hero: {
      title: 'Profitez d\'une visite d\'Argostoli en Tuk Tuk.',
      subtitle: 'Une balade inoubliable à travers les trésors cachés et les sites emblématiques',
      cta: 'Explorer les Tours',
      features: [
        'EXPÉRIENCE PRIVÉE ET PETITS GROUPES (max 4 invités)',
        '100% ÉLECTRIQUE ÉCO-RESPONSABLE',
        'ARRÊTS PHOTO',
        'GUIDE AUDIO MULTILINGUE',
        'CHOISISSEZ VOTRE LIEU DE PRISE EN CHARGE ET DE DÉPOSE'
      ]
    },
    tours: {
      title: '', subtitle: 'Choisissez le tour parfait pour vos besoins',
      exploreMore: 'Explorer Plus', backToTours: 'Retour aux Tours',
      oneHour: {
        title: 'Les Incontournables d\'Argostoli (1 heure)',
        description: 'Découvrez les points forts les plus importants d\'Argostoli à travers une expérience unique en Tuk-Tuk.\n\nL\'itinéraire combine les lieux les plus célèbres de la ville, des arrêts photo sélectionnés et un tour à travers les rues du centre.\n\nUne vue complète de l\'histoire, de l\'architecture et de la culture locale de la capitale de Céphalonie.'
      },
      twoHours: {
        title: 'Argostoli & Zone de Lassi (2 heures)',
        description: 'Ce tour combine la visite classique d\'Argostoli avec une promenade le long de la côte de la zone de Lassi, avec la possibilité d\'un arrêt baignade.\n\nLassi possède certaines des plages les plus célèbres de Céphalonie. De longues plages de sable aux eaux cristallines. Des plages bien aménagées avec bars, restaurants et transats, aux criques tranquilles et privées, tout est là pour être exploré.'
      },
      threeHours: {
        title: 'Argostoli, Lassi & Cave Gentilini (2 heures)',
        duration: '2 Heures',
        description: 'Profitez d\'une visite complète d\'Argostoli et de Lassi avec une visite d\'un domaine viticole local où vous aurez l\'occasion de déguster des vins kefaloniacs sélectionnés et de découvrir la riche tradition viticole de l\'île et les saveurs locales.\n\nVerre de bienvenue de Prosecco, dégustation guidée de 3 vins uniques et dégustation de 2 huiles d\'olive extra vierges et un vinaigre balsamique local.\nDuo de Dips avec pain pita chaud.\nVisite guidée dans les vignobles et la cave.'
      }
    },
    gallery: { title: '', subtitle: 'Photos de notre expérience en Tuk Tuk' },
    contact: {
      title: 'Nous Contacter', subtitle: 'Contactez-nous pour réservations et demandes',
      name: 'Votre Nom', email: 'Votre Email', phone: 'Numéro de Téléphone',
      tourSelection: 'Sélectionner le Tour', tourPlaceholder: 'Choisissez un tour',
      passengers: 'Nombre de Passagers', passengersPlaceholder: 'Sélectionnez les passagers',
      date: 'Date Préférée', time: 'Heure Préférée', message: 'Votre Message',
      send: 'Envoyer le Message', sending: 'Envoi en cours...',
      success: 'Message envoyé avec succès ! Nous vous contacterons bientôt.',
      error: 'Échec de l\'envoi du message. Veuillez réessayer.'
    },
    footer: {
      about: 'À Propos',
      aboutText: 'Explorez la ville d\'Argostoli avec un Tuk Tuk écologique.',
      quickLinks: 'Liens Rapides', followUs: 'Suivez-nous', rights: 'Tous droits réservés.'
    },
    tourDetail: {
      backButton: 'Retour aux Tours',
      aboutThisTour: 'À Propos de Ce Tour',
      tourRouteStops: 'Itinéraire et Arrêts du Tour',
      keyStops: 'Arrêts Principaux',
      clickToEnlarge: '(cliquez pour agrandir)',
      duration: 'Durée',
      hoursOne: '1 Heure',
      hoursTwo: '2 Heures',
      hoursThree: '2 Heures',
      groupSize: 'Taille du Groupe',
      upTo4: 'Jusqu\'à 4 passagers',
      startingPoint: 'Point de Départ',
      startingPointValue: 'Centre d\'Argostoli',
      availability: 'Disponibilité',
      availabilityValue: 'Tous les jours de 8h00 à 21h00 (chaque heure) – sur réservation',
      pricing: 'Tarifs',
      passengersCol: 'Passagers',
      priceCol: 'Prix',
      personSingular: 'personne',
      personPlural: 'personnes',
      totalLabel: 'total',
      perPersonUnit: '/personne',
      wineryFootnote: '*Entrée à la cave Gentilini incluse dans le prix',
      whatsIncluded: 'Ce Qui Est Inclus',
      included: {
        driver: 'Chauffeur/guide professionnel',
        pickup: 'Prise en charge et dépose à l\'hôtel',
        fees: 'Tous les frais et taxes inclus',
        water: 'Eau en bouteille',
        photoOps: 'Arrêts photo dans les lieux panoramiques',
        wifi: 'Wifi gratuit et chargeur pour smartphone',
        beachStop: 'Arrêt de 30-45 minutes sur la plage de votre choix',
        winery: 'Entrée prioritaire gratuite et visite guidée de la cave et des vignobles Gentilini'
      },
      readyToBook: 'Prêt à Réserver Ce Tour ?',
      contactPrompt: 'Contactez-nous pour réserver votre place ou poser des questions',
      contactUs: 'Nous Contacter',
      reachUs: 'Contactez-nous plus rapidement et facilement sur Viber ou WhatsApp.'
    },
    stops: {
      iLoveKefalonia: { name: 'I Love Kefalonia', description: 'Spot photo iconique avec le célèbre panneau "I ❤ Kefalonia" sur le front de mer d\'Argostoli.' },
      ww2Memorial: { name: 'Mémorial de la Seconde Guerre Mondiale', description: 'Mémorial en l\'honneur des soldats de la division italienne "Acqui".' },
      stTheodoreLighthouse: { name: 'Phare de Saint Théodore', description: 'Phare circulaire historique avec une vue panoramique sur la côte.' },
      argostoliSinkholes: { name: 'Gouffres d\'Argostoli', description: 'Gouffres géologiques uniques où l\'eau de mer s\'engouffre dans les rochers.' },
      centralSquare: { name: 'Place Centrale', description: 'Le cœur d\'Argostoli avec ses cafés, restaurants et sa vie locale.' },
      koutavosLagoon: { name: 'Lagune de Koutavos', description: 'Lagune paisible et protégée, abritant des tortues caouannes et des hérons.' },
      deBossetBridge: { name: 'Pont De Bosset', description: 'Pont en pierre historique traversant la lagune de Koutavos.' },
      carettaCarettaWatchpoint: { name: 'Point d\'Observation Possible de Caretta Caretta', description: 'Lieu d\'observation des tortues marines Caretta Caretta protégées.' },
      makrisGialosBeach: { name: 'Plage de Makris Gialos', description: 'Plage de sable populaire avec eaux cristallines et bars de plage.' },
      platisGialosBeach: { name: 'Plage de Platis Gialos', description: 'Plage de sable doré avec des vues magnifiques et des équipements.' },
      gentiliniWinery: { name: 'Cave Gentilini', description: 'Entrée prioritaire et visite guidée de la célèbre cave et des vignobles Gentilini.' }
    }
  },
  de: {
    nav: { home: 'Startseite', tours: 'Touren', gallery: 'Galerie', contact: 'Kontakt' },
    hero: {
      title: 'Genießen Sie eine Tour durch Argostoli in einem Tuk Tuk.',
      subtitle: 'Eine unvergessliche Fahrt durch verborgene Schätze und ikonische Sehenswürdigkeiten',
      cta: 'Touren Erkunden',
      features: [
        'PRIVATES & KLEINGRUPPEN-ERLEBNIS (max 4 Gäste)',
        '100% ELEKTRISCH UMWELTFREUNDLICH',
        'FOTO-STOPPS',
        'MEHRSPRACHIGER AUDIO-GUIDE',
        'WÄHLEN SIE IHREN ABHOL- UND ABSETZORT'
      ]
    },
    tours: {
      title: '', subtitle: 'Wählen Sie die perfekte Tour für Ihre Bedürfnisse',
      exploreMore: 'Mehr Erfahren', backToTours: 'Zurück zu den Touren',
      oneHour: {
        title: 'Argostoli Highlights (1 Stunde)',
        description: 'Entdecken Sie die wichtigsten Highlights von Argostoli durch ein einzigartiges Tuk-Tuk-Erlebnis.\n\nDie Route verbindet die berühmtesten Hotspots der Stadt, ausgewählte Fotostopps und eine Tour durch die Straßen der Innenstadt.\n\nEin vollständiger Blick auf die Geschichte, Architektur und lokale Kultur der Hauptstadt von Kefalonia.'
      },
      twoHours: {
        title: 'Argostoli & Lassi Gebiet (2 Stunden)',
        description: 'Diese Tour kombiniert die klassische Argostoli-Tour mit einer Fahrt entlang der Küste des Lassi-Gebiets, mit der Möglichkeit eines Badestopps.\n\nLassi hat einige der berühmtesten Strände Kefalonias. Lange Sandstrände mit kristallklarem Wasser. Von gut ausgestatteten Stränden mit Bars, Restaurants und Sonnenliegen bis hin zu ruhigen und privaten Buchten – hier gibt es alles zu entdecken.'
      },
      threeHours: {
        title: 'Argostoli, Lassi & Weingut Gentilini (2 Stunden)',
        duration: '2 Stunden',
        description: 'Genießen Sie eine vollständige Tour durch Argostoli und Lassi mit einem Besuch eines lokalen Weinguts, wo Sie die Möglichkeit haben, ausgewählte kefalonische Weine zu probieren und die reiche Weinbautradition der Insel und lokale Aromen zu entdecken.\n\nBegrüßungsglas Prosecco, geführte Verkostung von 3 einzigartigen Weinen und Verkostung von 2 nativen Olivenölen extra und einem lokalen Balsamico-Essig.\nDuo von Dips mit warmem Pitabrot.\nGeführte Tour durch die Weinberge und das Weingut.'
      }
    },
    gallery: { title: '', subtitle: 'Fotos von unserem Tuk Tuk Erlebnis' },
    contact: {
      title: 'Kontaktieren Sie Uns', subtitle: 'Kontaktieren Sie uns für Buchungen und Anfragen',
      name: 'Ihr Name', email: 'Ihre E-Mail', phone: 'Telefonnummer',
      tourSelection: 'Tour Auswählen', tourPlaceholder: 'Wählen Sie eine Tour',
      passengers: 'Anzahl der Passagiere', passengersPlaceholder: 'Passagiere wählen',
      date: 'Bevorzugtes Datum', time: 'Bevorzugte Zeit', message: 'Ihre Nachricht',
      send: 'Nachricht Senden', sending: 'Wird gesendet...',
      success: 'Nachricht erfolgreich gesendet! Wir werden Sie bald kontaktieren.',
      error: 'Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.'
    },
    footer: {
      about: 'Über Uns',
      aboutText: 'Erkunden Sie die Stadt Argostoli mit einem umweltfreundlichen Tuk Tuk.',
      quickLinks: 'Schnelllinks', followUs: 'Folgen Sie Uns', rights: 'Alle Rechte vorbehalten.'
    },
    tourDetail: {
      backButton: 'Zurück zu den Touren',
      aboutThisTour: 'Über Diese Tour',
      tourRouteStops: 'Tour-Route & Stopps',
      keyStops: 'Wichtige Stopps',
      clickToEnlarge: '(zum Vergrößern klicken)',
      duration: 'Dauer',
      hoursOne: '1 Stunde',
      hoursTwo: '2 Stunden',
      hoursThree: '2 Stunden',
      groupSize: 'Gruppengröße',
      upTo4: 'Bis zu 4 Passagiere',
      startingPoint: 'Startpunkt',
      startingPointValue: 'Argostoli Zentrum',
      availability: 'Verfügbarkeit',
      availabilityValue: 'Täglich von 8:00 bis 21:00 Uhr (zu jeder vollen Stunde) – nach Reservierung',
      pricing: 'Preise',
      passengersCol: 'Passagiere',
      priceCol: 'Preis',
      personSingular: 'Person',
      personPlural: 'Personen',
      totalLabel: 'gesamt',
      perPersonUnit: '/Person',
      wineryFootnote: '*Eintritt zum Weingut Gentilini im Preis enthalten',
      whatsIncluded: 'Was ist Inbegriffen',
      included: {
        driver: 'Professioneller Fahrer/Guide',
        pickup: 'Abholung und Rückbringung am Hotel',
        fees: 'Alle Gebühren und Steuern inbegriffen',
        water: 'Wasser in Flaschen',
        photoOps: 'Fotostopps an Aussichtspunkten',
        wifi: 'Kostenloses WLAN und Smartphone-Ladegerät',
        beachStop: 'Stopp von 30-45 Minuten an einem Strand Ihrer Wahl',
        winery: 'Kostenloser Vorrang-Eintritt und Führung im Weingut und in den Weinbergen Gentilini'
      },
      readyToBook: 'Bereit, Diese Tour zu Buchen?',
      contactPrompt: 'Kontaktieren Sie uns, um Ihren Platz zu reservieren oder Fragen zu stellen',
      contactUs: 'Kontaktieren Sie Uns',
      reachUs: 'Erreichen Sie uns schneller und einfacher über Viber oder WhatsApp.'
    },
    stops: {
      iLoveKefalonia: { name: 'I Love Kefalonia', description: 'Ikonischer Fotospot mit dem berühmten "I ❤ Kefalonia"-Schild an der Strandpromenade von Argostoli.' },
      ww2Memorial: { name: 'WW2-Denkmal', description: 'Denkmal zu Ehren der Soldaten der italienischen "Acqui"-Division.' },
      stTheodoreLighthouse: { name: 'Leuchtturm St. Theodore', description: 'Historischer runder Leuchtturm mit Panoramablick auf die Küste.' },
      argostoliSinkholes: { name: 'Argostoli Schlucker', description: 'Einzigartige geologische Schlucker, in die Meerwasser in die Felsen strömt.' },
      centralSquare: { name: 'Hauptplatz', description: 'Das Herz von Argostoli mit Cafés, Restaurants und lokalem Leben.' },
      koutavosLagoon: { name: 'Koutavos-Lagune', description: 'Friedliche, geschützte Lagune, Heimat von Unechten Karettschildkröten und Reihern.' },
      deBossetBridge: { name: 'De-Bosset-Brücke', description: 'Historische Steinbrücke über die Koutavos-Lagune.' },
      carettaCarettaWatchpoint: { name: 'Möglicher Aussichtspunkt für Caretta Caretta', description: 'Aussichtspunkt zur Beobachtung der geschützten Caretta-Caretta-Meeresschildkröten.' },
      makrisGialosBeach: { name: 'Makris-Gialos-Strand', description: 'Beliebter Sandstrand mit kristallklarem Wasser und Strandbars.' },
      platisGialosBeach: { name: 'Platis-Gialos-Strand', description: 'Strand mit goldenem Sand, atemberaubender Aussicht und Einrichtungen.' },
      gentiliniWinery: { name: 'Weingut Gentilini', description: 'Vorrang-Eintritt und Führung durch das renommierte Weingut und die Weinberge Gentilini.' }
    }
  }
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const t = translations[language];
  const value = useMemo(() => ({ language, setLanguage, t }), [language, t]);
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
